import columnDark from '../_assets/add-agent-dark.png';
import column from '../_assets/add-agent.png';
import agentAccountsDark from '../_assets/agent-accounts-dark.png';
import agentAccounts from '../_assets/agent-accounts.png';
import automationDark from '../_assets/agentic-automate-dark.png';
import automation from '../_assets/agentic-automate.png';
import appAccessDark from '../_assets/app-access-dark.png';
import appAccess from '../_assets/app-access.png';
import assignDark from '../_assets/assign-agent-dark.png';
import assignDetailsDark from '../_assets/assign-agent-details-dark.png';
import assignDetails from '../_assets/assign-agent-details.png';
import assign from '../_assets/assign-agent.png';
import pageHistoryDark from '../_assets/attribute1-dark.png';
import pageHistory from '../_assets/attribute1.png';
import attributionDiagramDark from '../_assets/attribute2-dark.png';
import attributionDiagram from '../_assets/attribute2.png';
import avatarDiagramDark from '../_assets/avatar-diagram-dark.png';
import avatarDiagram from '../_assets/avatar-diagram.png';
import avatarGroupDark from '../_assets/avatar-group-dark.png';
import avatarGroup from '../_assets/avatar-group.png';
import avatarsDark from '../_assets/avatars-dark.png';
import avatars from '../_assets/avatars.png';
import cardsDark from '../_assets/cards-dark.png';
import cardsDiagramDark from '../_assets/cards-diagram-dark.png';
import cardsDiagram from '../_assets/cards-diagram.png';
import cards from '../_assets/cards.png';
import chatDark from '../_assets/chat-dark.png';
import chat from '../_assets/chat.png';
import startCodingDark from '../_assets/coding-agent-dark.png';
import startCoding from '../_assets/coding-agent.png';
import commentsDark from '../_assets/comments-dark.png';
import comments from '../_assets/comments.png';
import hoverDark from '../_assets/hovercards-dark.png';
import hover from '../_assets/hovercards.png';
import insertionDark from '../_assets/insertion-dark.png';
import insertion from '../_assets/insertion.png';
import livePageAttributionDark from '../_assets/live_attribute-dark.png';
import livePageAttribution from '../_assets/live_attribute.png';
import mentionsDiagramDark from '../_assets/mentions-2-dark.png';
import mentionsDiagram from '../_assets/mentions-2.png';
import mentionsDark from '../_assets/mentions-dark.png';
import mentions from '../_assets/mentions.png';
import multimodalInputDark from '../_assets/multimodal-input-dark.png';
import multimodalInput from '../_assets/multimodal-input.png';
import sendVideoDark from '../_assets/send-video-dark.png';
import sendVideo from '../_assets/send-video.png';
import sessionStatusDark from '../_assets/session-dark.png';
import sessionStatus from '../_assets/session.png';
import shareContextDark from '../_assets/share-context-dark.png';
import shareContext from '../_assets/share-context.png';
import spaceAccessDark from '../_assets/space-access-dark.png';
import spaceAccess from '../_assets/space-access.png';
import telepointersDark from '../_assets/telepointers-dark.png';
import telepointers from '../_assets/telepointers.png';

type Pattern = {
	title: string;
	description: string | string[];
	image: { src: string; srcDark: string; alt: string };
	introduction?: string;
	additionalImages?: Array<{ src: string; srcDark: string; alt: string }>;
	details?: Array<{ image: { src: string; srcDark: string; alt: string }; description: string }>;
	guidance?: 'avatars' | 'cards' | 'hover' | 'attribution' | 'presence' | 'session' | 'automation';
};

export const patternGroups: Record<
	'identity' | 'presence' | 'workflows' | 'context' | 'conversation' | 'access',
	{ title: string; summary: string; patterns: Pattern[] }
> = {
	identity: {
		title: 'Identity',
		summary: 'Avatars, cards and attribution',
		patterns: [
			{
				title: 'Agent avatars',
				guidance: 'avatars',
				introduction:
					'The avatar hexagon’s proportions have been honed to feel balanced alongside circular human avatars; as such, the glyph is taller and thinner than the div it sits within. Avatar size varies from 16px to 46px depending on scenario. Different types of agents have a variety of design treatments:',
				additionalImages: [
					{
						src: avatars,
						srcDark: avatarsDark,
						alt: 'Four agent avatar categories: Rovo, a generic external agent, Atlassian and custom agents, and third-party agents with their own branding inside hexagons.',
					},
				],
				description: [
					'Rovo: The core platform agent',
					'A generic external agent: Used when less context is available',
					'Atlassian & custom agents: Use a limited but varied set of Atlassian icons and colours',
					'Third-party agents: Represented by their own brand livery but within the hexagon.',
				],
				image: {
					src: avatarDiagram,
					srcDark: avatarDiagramDark,
					alt: 'Agent avatar proportions: a 53.94-by-60.11-pixel hexagonal glyph sits within a 56-by-56-pixel container, appearing taller and narrower.',
				},
			},
			{
				title: 'Directory cards',
				details: [
					{
						image: {
							src: cardsDiagram,
							srcDark: cardsDiagramDark,
							alt: 'Agent card anatomy: a sunken header, raised body and default border, with a width of 220 to 320 pixels.',
						},
						description:
							'Cards have a variable width (with limits) and a standard vertical height. Long content in avatar names and descriptions is truncated. Be succinct and pithy in naming and describing agents.',
					},
				],
				guidance: 'cards',
				description:
					'Where additional information about the agent is required, use a “lanyard” style agent card to give extra context about agent owner, purpose and verification.',
				image: {
					src: cards,
					srcDark: cardsDark,
					alt: "Lanyard-style cards for Bug Report Assistant and Claude show each agent's avatar, name, owner, verification badge, description and actions.",
				},
			},
			{
				title: 'Hover cards',
				guidance: 'hover',
				description:
					'When hovering on agents in lists and pickers, use a simplified version of the agent card without additional actions such as chat to maintain focus on the task.',
				image: {
					src: hover,
					srcDark: hoverDark,
					alt: 'Hovering over Meeting summarizer in an assignee picker shows a simplified agent card with its name, owner, verification badge and description.',
				},
			},
			{
				title: 'Page history attribution',
				details: [
					{
						image: {
							src: attributionDiagram,
							srcDark: attributionDiagramDark,
							alt: 'Attribution diagram: a 32-pixel agent avatar is paired with a 16-pixel human avatar, offset by 2 pixels horizontally and vertically.',
						},
						description:
							'Always lead with the agent when attributing agent actions. The invoking human is shown in a smaller avatar with a 2px horizontal and vertical offset.',
					},
				],
				guidance: 'attribution',
				description:
					'Use a standard attribution format to represent agent edit actions. Use a large agent avatar with a smaller offset human avatar, with the agent presented as taking action with the invoking human.',
				image: {
					src: pageHistory,
					srcDark: pageHistoryDark,
					alt: 'Page version history credits agent edits to Rovo, Canva and Figma, each paired with the person who invoked the agent.',
				},
			},
			{
				title: 'Live edit attribution',
				description:
					'Use the same format for live edits, though in scenarios like these the limited space might mean the avatars are shown side by side at the same size.',
				image: {
					src: livePageAttribution,
					srcDark: livePageAttributionDark,
					alt: 'Highlighted page text is attributed to Rovo with Aoife Bourke, with their avatars displayed together beside the edit.',
				},
			},
		],
	},
	presence: {
		title: 'Presence',
		summary: 'Avatar groups, pointers and edits',
		patterns: [
			{
				title: 'Avatar groups',
				guidance: 'presence',
				description:
					'Agents must be shown in stacks of collaborators alongside humans when working on pages, whiteboards, boards and more.',
				image: {
					src: avatarGroup,
					srcDark: avatarGroupDark,
					alt: "A page's collaborator stack includes a circular human avatar, hexagonal Rovo and Claude avatars, and a count of additional collaborators.",
				},
			},
			{
				title: 'Telepointers',
				description:
					'Use a telepointer to represent agents as collaborators on rich content, in the same way as for human collaborators. Edits are shown in real time to provide visibility and control over agent action.',
				image: {
					src: telepointers,
					srcDark: telepointersDark,
					alt: 'Named pointers on a shared whiteboard identify Rovo and Claude alongside Liam and Roshini, using agent icons and human photos.',
				},
			},
			{
				title: 'Insertion points',
				description:
					'For text editing, use an insertion point and text highlight as a dynamic representation of any text editing by the agent.',
				image: {
					src: insertion,
					srcDark: insertionDark,
					alt: 'Named text insertion cursors distinguish Vernon, Rovo, Blocker checker and Claude, with agent icons identifying the agent cursors.',
				},
			},
		],
	},
	workflows: {
		title: 'Workflows',
		summary: 'Assignments, status and automation',
		patterns: [
			{
				title: 'Assign work to agent',
				details: [
					{
						image: {
							src: assignDetails,
							srcDark: assignDetailsDark,
							alt: "Jira's Assignee field shows Cursor with Roshini Malik, identifying the responsible agent and the person who assigned it.",
						},
						description:
							'On the work item details view, show the agent as assigned, with the human who assigned them as the supporting “with human”.',
					},
				],
				description:
					'Where agents are directly assigned a work item, they should be designated as the assignee of the work item to denote their responsibility.',
				image: {
					src: assign,
					srcDark: assignDark,
					alt: "Jira's assignee picker lists people and coding agents together, with circular human avatars and hexagonal agent avatars.",
				},
			},
			{
				title: 'Session status',
				guidance: 'session',
				description:
					'Whether assigned to the work item or merely running a work session, ensure the agent’s status is clearly visible. Humans must understand how agents are progressing and where they need guidance.',
				image: {
					src: sessionStatus,
					srcDark: sessionStatusDark,
					alt: 'Jira cards show agent session states such as Gaining context and Needs input, making progress and requests for guidance visible.',
				},
			},
			{
				title: 'Add agent to column',
				description:
					'Allow teams to make agents responsible for an entire step in their workflow. For instance, agents can also be added to workflows on Jira boards, picking up all work items added to an entire column.',
				image: {
					src: column,
					srcDark: columnDark,
					alt: "Cursor is added to a Jira board's Ready for Review column, with a Needs input status displayed beneath a work item.",
				},
			},
			{
				title: 'Agent autonomy',
				guidance: 'automation',
				description:
					'Provide humans with the means to make agents autonomous within strict bounds. Agents can be added to automation workflows and given a custom prompt. They then trigger automatically and complete work autonomously within set constraints.',
				image: {
					src: automation,
					srcDark: automationDark,
					alt: "Automation's Use agent step selects Cursor and provides a custom prompt directing it to review an epic and propose an engineering approach.",
				},
			},
		],
	},
	context: {
		title: 'Context',
		summary: 'Sharing context, code and video',
		patterns: [
			{
				title: 'Share context',
				description:
					'Context sharing must feel effortless. Provide humans with the means to share context in a single click. Not just a document but the web of associated context: links, related files, team information and more.',
				image: {
					src: shareContext,
					srcDark: shareContextDark,
					alt: "A Context panel's Share with agent menu offers Rovo, Cursor and Claude Desktop as destinations for the current work context.",
				},
			},
			{
				title: 'Start coding agent',
				description:
					'Enable the flow of work across surfaces by allowing users to switch to cloud or local agent harnesses. Work items can be passed directly to local and cloud agents to start coding with all the necessary context.',
				image: {
					src: startCoding,
					srcDark: startCodingDark,
					alt: "A Jira work item's Start coding agent menu offers local tools, including Rovo CLI, Cursor, Claude, Codex and VS Code.",
				},
			},
			{
				title: 'Send video to agent',
				description:
					'Ensure that context sharing is multi-modal. Even rich media such as video can be shared with agents to assist with content generation, coding tasks and more.',
				image: {
					src: sendVideo,
					srcDark: sendVideoDark,
					alt: 'A Loom Send to agent menu offers Claude Code, Codex, Cursor and other tools as destinations for a video-based brief.',
				},
			},
		],
	},
	conversation: {
		title: 'Conversation',
		summary: 'Mentions, comments, chat and multi-modal input',
		patterns: [
			{
				title: 'Mentions',
				details: [
					{
						image: {
							src: mentionsDiagram,
							srcDark: mentionsDiagramDark,
							alt: 'Mention styling uses neutral backgrounds for people and most agents, and a prominent Rovo brand background for Rovo, with an avatar and name in each mention.',
						},
						description:
							'Mentions use a simple lozenge with either the agent or the human avatar. Note that Rovo uses a more prominent dark neutral lozenge for visibility.',
					},
				],
				description:
					'Humans are able to @mention agents in documents and comments just as they can other humans, summoning the agent to take action or provide input.',
				image: {
					src: mentions,
					srcDark: mentionsDark,
					alt: 'Typing an at-mention in a page comment opens a picker containing both people and agents, distinguished by their avatar shapes.',
				},
			},
			{
				title: 'Comments',
				description:
					'Design agents for interaction beyond the chat box. Agents can reply to humans in all the surfaces that humans can, from work items to inline comments on Confluence pages and whiteboards.',
				image: {
					src: comments,
					srcDark: commentsDark,
					alt: 'Veronica mentions Tech Writer in a whiteboard comment; the agent replies in the same thread, with attribution linking its response to Veronica.',
				},
			},
			{
				title: 'Chat',
				description:
					'Equally, dedicated chat continues to be an important human-agent interaction modality for more in-depth conversations.',
				image: {
					src: chat,
					srcDark: chatDark,
					alt: 'A Policy guide agent answers a question about the Beaconstone Anytime policy in a chat panel alongside a whiteboard.',
				},
			},
			{
				title: 'Support multi-modal input',
				description:
					'Provide more modalities of input to an agent such as voice chat, sharing screenshots or uploading files, allowing humans to communicate in the most effective and accessible manner.',
				image: {
					src: multimodalInput,
					srcDark: multimodalInputDark,
					alt: 'Rovo listens to spoken input, showing an audio waveform, elapsed recording time and controls to cancel, stop or send the recording.',
				},
			},
		],
	},
	access: {
		title: 'Manage agent access',
		summary: 'Apps, spaces and accounts',
		patterns: [
			{
				title: 'App access',
				description:
					'Agents built on the Atlassian platform, including those created with Rovo Studio or Forge, can be granted roles and access to any app across the platform.',
				image: {
					src: appAccess,
					srcDark: appAccessDark,
					alt: 'App access settings for the Bug fixer agent list the Atlassian apps it can use and its role in each app.',
				},
			},
			{
				title: 'Space access',
				description:
					'Provide more granular access controls. Space administrators in Jira and Confluence can also manage agent access to their content, just as they can humans.',
				image: {
					src: spaceAccess,
					srcDark: spaceAccessDark,
					alt: 'Space access settings list people and agents together, with roles such as Administrator and Member.',
				},
			},
			{
				title: 'Agent accounts',
				description:
					'Support agent autonomy. Custom agents can either act using the permissions and access of their owner or be given their own account and unique permissions with which to take action.',
				image: {
					src: agentAccounts,
					srcDark: agentAccountsDark,
					alt: 'Agent account option explaining that the Policy checker agent can use its own account for consistent access, automation and Jira assignments.',
				},
			},
		],
	},
};
